"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { AnalyticsEvent, trackAnalytics } from "@/lib/analytics";
import { useLocale } from "@/lib/context/LocaleProvider";
import { useAuth } from "@/lib/context/AuthProvider";
import { useUI } from "@/lib/context/UIProvider";
import { useUtm } from "@/lib/context/UtmProvider";
import { useDocumentTitle } from "@/lib/hooks/useDocumentTitle";
import { useReportPageLoading } from "@/lib/hooks/useReportPageLoading";
import { resolveApiUrl } from "@/lib/api/client";
import {
  downloadUserCertificate,
  fetchUserCertificates,
  hasUsableCertificateAccess,
  type ApiUserCertificateItem,
} from "@/lib/api/certificates";
import {
  CERTIFICATE_PRODUCT_SLUG,
  createCommerceOrder,
  fetchCommerceProduct,
  formatCommerceMoney,
  MEMBERSHIP_CERTIFICATE_VARIANT_SLUG,
  payCommerceOrder,
  rememberCheckoutProductType,
  type ApiCommerceProductVariant,
} from "@/lib/api/commerce";
import {
  getMissingProfileNameFields,
  profileNameWarningMessage,
} from "@/lib/auth/profileErrors";
import { resolveServerError } from "@/lib/form/errors";
import { FormAlert } from "@/components/ui/FormAlert";
import { Check, Download } from "@/components/ui/icons";

function formatMessage(
  template: string,
  vars: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    String(vars[name] ?? ""),
  );
}

/** Landscape certificate frame while API / image are still loading. */
function CertificatePreviewSkeleton() {
  return (
    <div
      className="aspect-[4/3] w-full animate-pulse bg-[rgba(26,24,22,0.06)]"
      aria-hidden="true"
    >
      <div className="flex h-full flex-col justify-between p-6 sm:p-8">
        <div className="space-y-3">
          <span className="block h-3 w-[28%] rounded-[5px] bg-[rgba(26,24,22,0.08)]" />
          <span className="block h-5 w-[55%] rounded-[6px] bg-[rgba(26,24,22,0.1)]" />
        </div>
        <div className="mx-auto space-y-2.5 text-center">
          <span className="mx-auto block h-4 w-[42%] rounded-[5px] bg-[rgba(26,24,22,0.08)]" />
          <span className="mx-auto block h-7 w-[68%] rounded-[8px] bg-[rgba(26,24,22,0.12)]" />
          <span className="mx-auto block h-3.5 w-[36%] rounded-[5px] bg-[rgba(26,24,22,0.07)]" />
        </div>
        <div className="flex justify-between gap-4">
          <span className="block h-3 w-[22%] rounded-[5px] bg-[rgba(26,24,22,0.07)]" />
          <span className="block h-3 w-[18%] rounded-[5px] bg-[rgba(26,24,22,0.07)]" />
        </div>
      </div>
    </div>
  );
}

/** /certificates — WITA membership certificate offer / issued cert. */
export function CertificatesScreen() {
  const { t, locale, hydrated: localeHydrated } = useLocale();
  const { user, isAuthenticated, isPro, refreshUser } = useAuth();
  const { openProModal } = useUI();
  const { utm } = useUtm();
  useDocumentTitle(t.certsTitle);

  const [issued, setIssued] = useState<ApiUserCertificateItem | null>(null);
  const [variant, setVariant] = useState<ApiCommerceProductVariant | null>(null);
  /** Ownership resolved at least once — until then left column stays on skeleton. */
  const [ready, setReady] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [loadError, setLoadError] = useState<{ key: string; message: string | null } | null>(
    null,
  );
  const [ctaError, setCtaError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadErrorState, setDownloadErrorState] = useState<{
    key: string;
    message: string | null;
  } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  /** `previewSrc` value for which the image has finished load/error. */
  const [loadedPreviewSrc, setLoadedPreviewSrc] = useState<string | null>(null);

  // One key per load. Errors are tagged with it and derived back out, so a new
  // load clears the old message without an effect resetting state.
  const requestKey = `${locale}|${isAuthenticated}|${reloadKey}`;
  const error = loadError?.key === requestKey ? loadError.message : null;
  const downloadError =
    downloadErrorState?.key === requestKey ? downloadErrorState.message : null;
  const setError = (message: string | null) => setLoadError({ key: requestKey, message });
  const setDownloadError = (message: string | null) =>
    setDownloadErrorState({ key: requestKey, message });

  useReportPageLoading(!localeHydrated || !ready);

  const missingProfileNameFields = getMissingProfileNameFields(
    user?.firstName,
    user?.lastName,
  );
  /** Active / cancelled access — download & verify as owned. */
  const purchased = hasUsableCertificateAccess(issued);
  /** Prior cert exists but access ended (revoked / expired) → show repurchase. */
  const canRepurchase = issued != null && !purchased;
  const profileComplete = missingProfileNameFields == null;

  useEffect(() => {
    /* istanbul ignore next -- locale hydration resolves before the first client paint */
    if (!localeHydrated) return;

    let cancelled = false;

    (async () => {
      try {
        let first: ApiUserCertificateItem | null = null;

        if (isAuthenticated) {
          const items = await fetchUserCertificates();
          if (cancelled) return;
          first = items[0] ?? null;
          setIssued(first);

          // Owned cert — no storefront price needed.
          if (hasUsableCertificateAccess(first)) {
            setVariant(null);
            setPriceLoading(false);
            return;
          }
        } else {
          setIssued(null);
        }

        // Guests and unpaid/expired owners load the public certificate offer.
        setPriceLoading(true);
        const data = await fetchCommerceProduct(
          CERTIFICATE_PRODUCT_SLUG,
          locale,
        );
        if (cancelled) return;
        const selected =
          (data.variants ?? []).find(
            (v) => v.slug === MEMBERSHIP_CERTIFICATE_VARIANT_SLUG,
          ) ?? null;
        if (!selected) {
          console.error(
            `Commerce product "${CERTIFICATE_PRODUCT_SLUG}" has no variant "${MEMBERSHIP_CERTIFICATE_VARIANT_SLUG}"`,
          );
          setVariant(null);
          setError(t.authErrorFallback);
          return;
        }
        setVariant(selected);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load certificates screen", err);
        setIssued(null);
        setVariant(null);
        setError(resolveServerError(err, t).form);
      } finally {
        if (!cancelled) {
          setPriceLoading(false);
          setReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Profile name is read from `user` at render time; navigating back from
    // /account remounts this screen and refreshes the issued preview.
    // `t` tracks locale copy used in error handling (locale is already a dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid refetch on unrelated t identity churn
  }, [locale, localeHydrated, reloadKey, isAuthenticated]);

  const onCta = async () => {
    /* istanbul ignore next -- the CTA is disabled while submitting */
    if (submitting) return;

    // Certificate checkout requires an active subscription (trial or paid).
    // Guests and signed-in users without one get the Pro dialog first.
    if (!isPro) {
      openProModal();
      return;
    }

    /* istanbul ignore next -- a missing variant already renders the error state instead of the CTA */
    if (!variant) {
      setCtaError(t.authErrorFallback);
      return;
    }

    setCtaError(null);
    setSubmitting(true);
    try {
      const order = await createCommerceOrder(variant.id);

      trackAnalytics(AnalyticsEvent.InitiateCheckout, {
        product_type: "certificate",
        value: variant.effectiveAmount,
        currency: variant.currency,
        variant_id: variant.id,
      });

      const payment = await payCommerceOrder(order.id, "stripe", null, utm);

      if (payment.checkoutUrl) {
        rememberCheckoutProductType("certificate");
        window.location.href = payment.checkoutUrl;
        return;
      }

      // Local/fake gateway may mark the order paid without a redirect URL.
      await refreshUser();
      setReloadKey((k) => k + 1);
    } catch (err) {
      console.error("Failed to start certificate checkout", err);
      setCtaError(resolveServerError(err, t).form);
    } finally {
      setSubmitting(false);
    }
  };

  const onDownload = async () => {
    /* istanbul ignore next -- the download button only renders with a signed URL */
    if (!issued?.downloadUrl || downloading) return;

    setDownloadError(null);
    setDownloading(true);
    try {
      await downloadUserCertificate(
        issued.downloadUrl,
        `membership-certificate-${issued.publicNumber}.png`,
      );
    } catch (err) {
      console.error("Failed to download certificate", err);
      setDownloadError(
        resolveServerError(err, t, { fallback: t.certsDownloadError }).form,
      );
    } finally {
      setDownloading(false);
    }
  };

  const showPriceSkeleton =
    ready && !purchased && (priceLoading || (!variant && !error));
  const priceWas =
    ready && !purchased && !showPriceSkeleton && variant?.discountEnabled
      ? formatCommerceMoney(variant.amount, variant.currency, locale)
      : null;
  const priceNow =
    ready && !purchased && !showPriceSkeleton && variant
      ? formatCommerceMoney(variant.effectiveAmount, variant.currency, locale)
      : null;

  // Real preview only when the profile name is complete — otherwise empty
  // first/last name would render a blank certificate.
  const showIssuedPreview =
    ready && purchased && profileComplete && Boolean(issued?.previewUrl);
  const previewSrc =
    showIssuedPreview && issued?.previewUrl
      ? resolveApiUrl(issued.previewUrl)
      : ready
        ? "/certificate/demo.png"
        : null;
  const memberName =
    issued?.recipientDisplayName?.trim() ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  const previewLoaded = previewSrc != null && loadedPreviewSrc === previewSrc;
  const showPreviewSkeleton = !ready || !previewLoaded;

  return (
    <section className="animate-fade-up px-5 pt-7">
      <h1 className="mb-2 text-[32px] font-medium leading-[1.05] tracking-[-1.28px]">
        {t.certsTitle}
      </h1>
      <p className="mb-5 text-[15px] leading-[1.45] text-ink-3 [text-wrap:pretty]">
        {t.certsDesc}
      </p>

      <div className="card-shadow overflow-hidden rounded-[20px] border border-[rgba(26,24,22,0.05)] bg-white">
        {/* Preview first in DOM for mobile; lg:order restores text | preview. */}
        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
          <div className="bg-surface px-5 py-7 sm:px-7 lg:order-2 lg:border-l lg:border-[rgba(26,24,22,0.07)]">
            <div className="relative overflow-hidden rounded-[18px] border border-[rgba(26,24,22,0.07)] bg-white">
              {showPreviewSkeleton ? <CertificatePreviewSkeleton /> : null}
              {previewSrc ? (
                <img
                  key={previewSrc}
                  ref={(img) => {
                    // Cached images can be `complete` before `onLoad` is attached.
                    if (
                      img?.complete &&
                      img.naturalWidth > 0 &&
                      loadedPreviewSrc !== previewSrc
                    ) {
                      setLoadedPreviewSrc(previewSrc);
                    }
                  }}
                  src={previewSrc}
                  alt={
                    showIssuedPreview
                      ? memberName
                        ? `${t.certsPreviewAlt} — ${memberName}`
                        : t.certsPreviewAlt
                      : t.certsSpecimenAlt
                  }
                  onLoad={() => setLoadedPreviewSrc(previewSrc)}
                  onError={() => {
                    console.error("Failed to load certificate preview", previewSrc);
                    setLoadedPreviewSrc(previewSrc);
                  }}
                  className={
                    previewLoaded
                      ? "h-auto w-full"
                      : "pointer-events-none absolute inset-0 h-full w-full opacity-0"
                  }
                />
              ) : null}
              {previewLoaded && !showIssuedPreview ? (
                <>
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span className="-rotate-[18deg] select-none text-[28px] font-bold uppercase tracking-[0.35em] text-ink/25 sm:text-[34px]">
                      {t.certsSpecimen}
                    </span>
                  </div>
                  <div className="pointer-events-none absolute left-3.5 top-3.5 rounded-full bg-ink/85 px-3 py-1 text-[11px] font-semibold tracking-[0.4px] text-white">
                    {t.certsSpecimen}
                  </div>
                </>
              ) : null}
            </div>
            <p className="mt-3 text-center text-[12.5px] leading-[1.45] text-muted [text-wrap:pretty]">
              {showPreviewSkeleton
                ? "\u00a0"
                : showIssuedPreview
                  ? t.certsPreviewCaption
                  : t.certsSpecimenCaption}
            </p>
          </div>

          <div className="border-l-[6px] border-l-gold border-t border-t-[rgba(26,24,22,0.07)] px-5 py-7 sm:px-7 sm:py-8 lg:order-1 lg:border-t-0">
            {!ready ? (
              <div className="space-y-6" aria-hidden="true">
                <div className="space-y-3">
                  <span className="block h-4 w-full max-w-[320px] animate-pulse rounded-[6px] bg-[rgba(26,24,22,0.08)]" />
                  <span className="block h-4 w-full max-w-[240px] animate-pulse rounded-[6px] bg-[rgba(26,24,22,0.06)]" />
                  <span className="mt-1 block h-[72px] w-full animate-pulse rounded-[14px] bg-[rgba(26,24,22,0.06)]" />
                </div>
                <div className="space-y-2.5">
                  <span className="block h-3 w-[120px] animate-pulse rounded-[5px] bg-[rgba(26,24,22,0.08)]" />
                  <span className="block h-3.5 w-full max-w-[280px] animate-pulse rounded-[5px] bg-[rgba(26,24,22,0.06)]" />
                  <span className="block h-3.5 w-full max-w-[260px] animate-pulse rounded-[5px] bg-[rgba(26,24,22,0.06)]" />
                  <span className="block h-3.5 w-full max-w-[240px] animate-pulse rounded-[5px] bg-[rgba(26,24,22,0.06)]" />
                </div>
                <span className="block h-[46px] w-full max-w-[220px] animate-pulse rounded-full bg-[rgba(26,24,22,0.1)]" />
              </div>
            ) : (
              <>
                {purchased ? (
                  <div className="space-y-3">
                    <p className="text-[15px] leading-[1.55] text-ink-2 [text-wrap:pretty]">
                      {t.certsPurchasedNote}
                    </p>
                    {profileComplete ? (
                      issued?.publicNumber ? (
                        <div className="rounded-[14px] border border-accent/15 bg-accent-soft px-4 py-3">
                          <p className="text-[13.5px] font-medium text-ink">
                            {formatMessage(t.certsSerialNumber, {
                              serial: issued.publicNumber,
                            })}
                          </p>
                          <Link
                            href={`/certificates/verify/${encodeURIComponent(issued.publicNumber)}`}
                            className="mt-2 inline-flex text-[13px] font-semibold text-accent transition hover:opacity-80"
                          >
                            {t.certsVerifyOpenLink}
                          </Link>
                        </div>
                      ) : (
                        <p>
                          <Link
                            href="/certificates/verify"
                            className="text-[13.5px] font-semibold text-accent transition hover:opacity-80"
                          >
                            {t.certsVerifyOpenLink}
                          </Link>
                        </p>
                      )
                    ) : null}
                  </div>
                ) : (
                  <p className="text-[15px] leading-[1.55] text-ink-2 [text-wrap:pretty]">
                    {canRepurchase ? t.certsRepurchaseNote : t.certsObserverNote}
                  </p>
                )}

                <h2 className="mt-6 text-[11px] font-bold uppercase tracking-[0.7px] text-muted-2">
                  {purchased ? t.certsIncludesTitle : t.certsWhyTitle}
                </h2>

                <ul className="mt-3.5 space-y-2.5">
                  {t.certsBenefits.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-[14px] leading-[1.45] text-ink-2"
                    >
                      <span className="mt-0.5 flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-accent-soft text-accent">
                        <Check size={10} />
                      </span>
                      <span className="[text-wrap:pretty]">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 space-y-4">
                  {purchased ? (
                    <>
                      {!profileComplete && missingProfileNameFields ? (
                        <div
                          role="status"
                          className="rounded-[16px] border border-gold/35 bg-gold/15 px-4 py-3.5"
                        >
                          <p className="text-[13.5px] leading-[1.5] text-ink [text-wrap:pretty]">
                            {profileNameWarningMessage(missingProfileNameFields, t)}
                          </p>
                          <Link
                            href="/account"
                            className="mt-2.5 inline-flex text-[13.5px] font-semibold text-accent transition hover:opacity-80"
                          >
                            {t.certsProfileIncompleteLink}
                          </Link>
                        </div>
                      ) : (
                        <>
                          {downloadError ? (
                            <FormAlert>{downloadError}</FormAlert>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => {
                              void onDownload();
                            }}
                            disabled={downloading || !issued?.downloadUrl}
                            className="flex h-[46px] w-full items-center justify-center gap-2 rounded-full bg-accent px-6 text-[14px] font-semibold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[220px]"
                          >
                            <Download size={16} />
                            {downloading ? t.certsDownloading : t.certsDownload}
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="rounded-[16px] border border-accent/15 bg-accent-soft px-4 py-3.5">
                        <p className="text-[11px] font-bold uppercase tracking-[0.7px] text-accent">
                          {t.certsPlanLabel}
                        </p>
                        {error ? (
                          <div className="mt-2 space-y-2.5">
                            <FormAlert>{error}</FormAlert>
                            <button
                              type="button"
                              onClick={() => setReloadKey((k) => k + 1)}
                              className="rounded-full border border-[rgba(26,24,22,0.16)] bg-surface px-3.5 py-[7px] text-[12.5px] font-medium text-ink transition hover:bg-[rgba(26,24,22,0.04)]"
                            >
                              {t.retry}
                            </button>
                          </div>
                        ) : showPriceSkeleton ? (
                          <div
                            className="mt-2.5 flex items-baseline gap-2.5"
                            aria-hidden="true"
                          >
                            <span className="inline-block h-4 w-[52px] animate-pulse rounded-[6px] bg-[rgba(26,24,22,0.08)]" />
                            <span className="inline-block h-7 w-[88px] animate-pulse rounded-[8px] bg-[rgba(26,24,22,0.1)]" />
                          </div>
                        ) : (
                          <p className="mt-1.5 text-[26px] font-semibold tracking-[-0.6px] text-ink">
                            {priceWas ? (
                              <span className="mr-2 text-[15px] font-normal text-muted-2 line-through">
                                {priceWas}
                              </span>
                            ) : null}
                            {priceNow}
                          </p>
                        )}
                      </div>

                      <p className="text-[13.5px] leading-[1.55] text-ink-3 [text-wrap:pretty]">
                        {t.certsNotInPro}
                      </p>

                      {ctaError ? <FormAlert>{ctaError}</FormAlert> : null}

                      <button
                        type="button"
                        onClick={() => {
                          void onCta();
                        }}
                        disabled={
                          isPro
                            ? priceLoading ||
                              submitting ||
                              Boolean(error) ||
                              !variant
                            : false
                        }
                        className="flex h-[46px] w-full items-center justify-center rounded-full bg-accent px-6 text-[14px] font-semibold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[220px]"
                      >
                        {submitting ? t.authSubmitting : t.certsCta}
                      </button>

                      <p className="flex items-center gap-1.5 text-[12.5px] text-muted">
                        <LockIcon />
                        {t.certsSecurePay}
                      </p>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function LockIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="flex-none"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}
